/**
 * AiGeneratorModal.jsx
 *
 * Shared AI gallery-settings generator.
 * Used in two contexts:
 *
 *   mode="create"  (CrudApp)   — generates settings for a brand-new rule.
 *   mode="update"  (RulesDetails) — suggests changes to the active rule's settings.
 *
 * Props:
 *   open            boolean
 *   onClose         () => void
 *   onApply         (settings: object, title: string) => void
 *   currentSettings object  optional — passed for context in "update" mode
 *   mode            'create' | 'update'
 */

import { useState, useEffect, useRef, useCallback } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import {
    X,
    Sparkles,
    Upload,
    ImageIcon,
    Loader2,
    AlertCircle,
    ExternalLink,
    ChevronRight,
    Check,
    RefreshCw,
} from "lucide-react";
import { getAiStatus, generateSettings } from "../api/ai";
import { SEARCH_INDEX } from "../constants/searchIndex";

// ── Label lookup ──────────────────────────────────────────────────────────────
const KEY_LABELS = Object.fromEntries(SEARCH_INDEX.map((e) => [e.key, e.label]));

function getLabel(key) {
    return KEY_LABELS[key] || key.replace(/([A-Z])/g, " $1").trim();
}

function formatValue(value) {
    if (typeof value === "boolean") return value ? "Enabled" : "Disabled";
    if (typeof value === "number")  return String(value);
    if (typeof value === "string") {
        return value
            .replace(/-/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    }
    return String(value);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusMessage({ status, onClose }) {
    const opts           = window.WPBean_PGS_Options || {};
    const connectorsUrl  = opts.connectorsUrl  || "#";
    const aiSettingsUrl  = opts.aiSettingsUrl  || "#";
    const hasWp7         = status?.reason !== "ai_not_supported";

    return (
        <div className="flex flex-col items-center justify-center py-10 px-6 text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-amber-500" />
            </div>
            <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {__("AI provider not configured", "wpbean-product-gallery-slider-for-woocommerce")}
                </h3>
                <p className="text-sm text-gray-500 max-w-xs">
                    {__("Add an API key for OpenAI, Anthropic Claude, or Google Gemini to start generating gallery settings.", "wpbean-product-gallery-slider-for-woocommerce")}
                </p>
            </div>

            {/* Primary CTA: plugin AI Settings page */}
            <a
                href={aiSettingsUrl}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 no-underline transition-colors"
            >
                {__("Add API Key in AI Settings", "wpbean-product-gallery-slider-for-woocommerce")}
                <ExternalLink className="w-3.5 h-3.5" />
            </a>

            {/* Secondary: Connectors (WP 7.0+ only) */}
            {hasWp7 && (
                <a
                    href={connectorsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 no-underline transition-colors"
                >
                    {__("Or configure in Settings → Connectors", "wpbean-product-gallery-slider-for-woocommerce")}
                    <ExternalLink className="w-3 h-3" />
                </a>
            )}

            <button
                onClick={onClose}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
                {__("Close", "wpbean-product-gallery-slider-for-woocommerce")}
            </button>
        </div>
    );
}

function ImageDropZone({ image, onImage, onClear }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const handleFile = useCallback((file) => {
        if (!file || !file.type.startsWith("image/")) return;
        const reader = new FileReader();
        reader.onload = (e) => onImage(e.target.result);
        reader.readAsDataURL(file);
    }, [onImage]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files[0]);
    };

    if (image) {
        return (
            <div className="relative rounded-xl overflow-hidden border border-gray-200">
                <img
                    src={image}
                    alt="Reference"
                    className="w-full h-36 object-cover"
                />
                <button
                    type="button"
                    onClick={onClear}
                    className="absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900/60 text-white hover:bg-gray-900/80 transition-colors"
                    aria-label={__("Remove image", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
        );
    }

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            className={[
                "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-7 cursor-pointer transition-colors",
                dragging ? "border-blue-400 bg-blue-50" : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100",
            ].join(" ")}
        >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                <ImageIcon className="w-5 h-5 text-gray-400" />
            </div>
            <div className="text-center">
                <p className="text-sm font-medium text-gray-700">
                    {__("Drop a reference image here", "wpbean-product-gallery-slider-for-woocommerce")}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                    {__("or click to browse", "wpbean-product-gallery-slider-for-woocommerce")}
                </p>
            </div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => handleFile(e.target.files[0])}
            />
        </div>
    );
}

function SuggestionRow({ item, checked, onChange }) {
    return (
        <label className="flex items-start gap-3 py-2.5 cursor-pointer group">
            <div className="mt-0.5 shrink-0">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(item.key, e.target.checked)}
                    className="sr-only"
                />
                <div
                    className={[
                        "flex h-4.5 w-4.5 items-center justify-center rounded border transition-colors",
                        checked
                            ? "border-blue-600 bg-blue-600"
                            : "border-gray-300 bg-white group-hover:border-blue-400",
                    ].join(" ")}
                    style={{ width: 18, height: 18 }}
                >
                    {checked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                </div>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-gray-800 leading-snug">
                    {getLabel(item.key)}
                </p>
                <p className="text-[11.5px] text-gray-400 mt-0.5">
                    {item.from !== undefined ? (
                        <>
                            <span className="line-through">{formatValue(item.from)}</span>
                            <ChevronRight className="inline w-3 h-3 mx-1 text-gray-300" />
                            <span className="text-blue-600 font-medium">{formatValue(item.to)}</span>
                        </>
                    ) : (
                        <span className="text-blue-600 font-medium">{formatValue(item.to)}</span>
                    )}
                </p>
            </div>
        </label>
    );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export default function AiGeneratorModal({ open, onClose, onApply, currentSettings = {}, mode = "update" }) {
    const [aiStatus, setAiStatus]       = useState(null); // null = loading, object otherwise
    const [step, setStep]               = useState("input"); // 'input' | 'loading' | 'preview'
    const [prompt, setPrompt]           = useState("");
    const [image, setImage]             = useState(null);  // base64 data URL
    const [error, setError]             = useState(null);
    const [aiTitle, setAiTitle]         = useState("");
    const [aiReasoning, setAiReasoning] = useState("");
    const [suggestions, setSuggestions] = useState([]);    // [{key, from, to}]
    const [selected, setSelected]       = useState({});    // key → bool

    // Reset when modal opens
    useEffect(() => {
        if (!open) return;
        setStep("input");
        setPrompt("");
        setImage(null);
        setError(null);
        setAiTitle("");
        setAiReasoning("");
        setSuggestions([]);
        setSelected({});
    }, [open]);

    // Fetch AI status once on first open
    useEffect(() => {
        if (!open || aiStatus !== null) return;
        getAiStatus()
            .then(setAiStatus)
            .catch(() => setAiStatus({ available: false, reason: "error" }));
    }, [open, aiStatus]);

    const handleGenerate = async () => {
        if (!prompt.trim() && !image) return;
        setError(null);
        setStep("loading");

        try {
            const payload = {
                prompt,
                ...(image && { image_data: image }),
                ...(Object.keys(currentSettings).length > 0 && { current_settings: currentSettings }),
            };

            const result = await generateSettings(payload);

            // Build diff vs current (or empty baseline)
            const diff = Object.entries(result.settings).map(([key, to]) => ({
                key,
                from: currentSettings[key],
                to,
            }));

            setAiTitle(result.title || "");
            setAiReasoning(result.reasoning || "");
            setSuggestions(diff);
            setSelected(Object.fromEntries(diff.map((d) => [d.key, true])));
            setStep("preview");
        } catch (err) {
            setError(err?.message || __("AI generation failed. Please try again.", "wpbean-product-gallery-slider-for-woocommerce"));
            setStep("input");
        }
    };

    const handleToggle = (key, val) => {
        setSelected((prev) => ({ ...prev, [key]: val }));
    };

    const handleSelectAll = (val) => {
        setSelected(Object.fromEntries(suggestions.map((s) => [s.key, val])));
    };

    const handleApply = () => {
        const chosenSettings = Object.fromEntries(
            suggestions
                .filter((s) => selected[s.key])
                .map((s) => [s.key, s.to])
        );
        onApply(chosenSettings, aiTitle);
        onClose();
    };

    const selectedCount = Object.values(selected).filter(Boolean).length;

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100050] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
                onClick={onClose}
            />

            {/* Panel */}
            <div className="relative z-10 w-full max-w-[480px] rounded-2xl bg-white shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-violet-600">
                            <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h2 className="text-[14px] font-semibold text-gray-900 leading-none">
                                {__("AI Gallery Assistant", "wpbean-product-gallery-slider-for-woocommerce")}
                            </h2>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                                {step === "preview"
                                    ? __("Review suggested changes", "wpbean-product-gallery-slider-for-woocommerce")
                                    : __("Describe the gallery style you want", "wpbean-product-gallery-slider-for-woocommerce")}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                        aria-label={__("Close", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto" style={{ maxHeight: "70vh" }}>
                    {/* Loading AI status */}
                    {aiStatus === null && (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
                            <p className="text-sm text-gray-400">
                                {__("Checking AI availability…", "wpbean-product-gallery-slider-for-woocommerce")}
                            </p>
                        </div>
                    )}

                    {/* AI not available */}
                    {aiStatus && !aiStatus.available && (
                        <StatusMessage status={aiStatus} onClose={onClose} />
                    )}

                    {/* Input step */}
                    {aiStatus?.available && step === "input" && (
                        <div className="px-5 py-5 space-y-4">
                            {error && (
                                <div className="flex items-start gap-2.5 rounded-lg bg-red-50 px-3.5 py-3 text-sm text-red-700">
                                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                    {error}
                                </div>
                            )}

                            <div>
                                <label className="block text-[12px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                    {__("Describe the style you want", "wpbean-product-gallery-slider-for-woocommerce")}
                                </label>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder={__("e.g. Large hero images with thumbnails on the left, subtle zoom, clean lightbox — like a luxury fashion store.", "wpbean-product-gallery-slider-for-woocommerce")}
                                    rows={4}
                                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 text-sm text-gray-800 placeholder:text-gray-400 resize-none focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500/20 hover:border-gray-300 transition-colors"
                                />
                            </div>

                            <div>
                                <label className="block text-[12px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                    {__("Reference image", "wpbean-product-gallery-slider-for-woocommerce")}
                                    <span className="ml-1.5 font-normal normal-case text-gray-400">
                                        {__("(optional)", "wpbean-product-gallery-slider-for-woocommerce")}
                                    </span>
                                </label>
                                <ImageDropZone
                                    image={image}
                                    onImage={setImage}
                                    onClear={() => setImage(null)}
                                />
                                <p className="mt-1.5 text-[11px] text-gray-400">
                                    {__("Upload a screenshot of a gallery style you like — the AI will analyse it.", "wpbean-product-gallery-slider-for-woocommerce")}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Loading step */}
                    {step === "loading" && (
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                            <div className="relative">
                                <div className="w-14 h-14 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-blue-500" />
                                </div>
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-medium text-gray-700">
                                    {__("Generating gallery settings…", "wpbean-product-gallery-slider-for-woocommerce")}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    {__("This usually takes a few seconds.", "wpbean-product-gallery-slider-for-woocommerce")}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Preview step */}
                    {step === "preview" && (
                        <div className="px-5 py-5 space-y-4">
                            {/* Reasoning */}
                            {aiReasoning && (
                                <div className="rounded-xl bg-blue-50 px-4 py-3">
                                    <div className="flex items-start gap-2.5">
                                        <Sparkles className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                                        <p className="text-[12.5px] text-blue-800 leading-relaxed">
                                            {aiReasoning}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Rule title (for create mode) */}
                            {mode === "create" && aiTitle && (
                                <div>
                                    <label className="block text-[12px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        {__("Rule name", "wpbean-product-gallery-slider-for-woocommerce")}
                                    </label>
                                    <input
                                        type="text"
                                        value={aiTitle}
                                        onChange={(e) => setAiTitle(e.target.value)}
                                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500/20 hover:border-gray-300 transition-colors"
                                    />
                                </div>
                            )}

                            {/* Suggestions list */}
                            {suggestions.length === 0 ? (
                                <div className="py-6 text-center">
                                    <p className="text-sm text-gray-500">
                                        {__("The AI didn't suggest any changes. Try a more specific prompt.", "wpbean-product-gallery-slider-for-woocommerce")}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-[12px] font-semibold text-gray-500 uppercase tracking-wider">
                                            {suggestions.length === 1
                                                ? __("1 suggested change", "wpbean-product-gallery-slider-for-woocommerce")
                                                : `${suggestions.length} ${__("suggested changes", "wpbean-product-gallery-slider-for-woocommerce")}`}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => handleSelectAll(selectedCount < suggestions.length)}
                                            className="text-[11px] text-blue-600 hover:text-blue-700 font-medium transition-colors"
                                        >
                                            {selectedCount < suggestions.length
                                                ? __("Select all", "wpbean-product-gallery-slider-for-woocommerce")
                                                : __("Deselect all", "wpbean-product-gallery-slider-for-woocommerce")}
                                        </button>
                                    </div>

                                    <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100 px-4">
                                        {suggestions.map((item) => (
                                            <SuggestionRow
                                                key={item.key}
                                                item={item}
                                                checked={!!selected[item.key]}
                                                onChange={handleToggle}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {aiStatus?.available && step !== "loading" && (
                    <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50/80">
                        {step === "preview" ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setStep("input")}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                    {__("Try again", "wpbean-product-gallery-slider-for-woocommerce")}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleApply}
                                    disabled={selectedCount === 0}
                                    className={[
                                        "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                                        selectedCount === 0
                                            ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                            : "bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700 shadow-sm active:scale-[0.98]",
                                    ].join(" ")}
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    {mode === "create"
                                        ? __("Create Rule", "wpbean-product-gallery-slider-for-woocommerce")
                                        : selectedCount === 1
                                            ? __("Apply 1 Change", "wpbean-product-gallery-slider-for-woocommerce")
                                            : `${__("Apply", "wpbean-product-gallery-slider-for-woocommerce")} ${selectedCount} ${__("Changes", "wpbean-product-gallery-slider-for-woocommerce")}`}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    {__("Cancel", "wpbean-product-gallery-slider-for-woocommerce")}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleGenerate}
                                    disabled={!prompt.trim() && !image}
                                    className={[
                                        "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                                        !prompt.trim() && !image
                                            ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                            : "bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700 shadow-sm active:scale-[0.98]",
                                    ].join(" ")}
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    {__("Generate", "wpbean-product-gallery-slider-for-woocommerce")}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
